/**
 * The platform's Appearance vocabulary: one place for the colour presets,
 * fonts, text sizes, corners and spacing that Settings -> Appearance offers.
 *
 * Used by
 *   contexts/ThemeContext.tsx                 the dashboard (vars on :root)
 *   components/appearance/*                   the controls (Settings page, Brand drawer)
 *   features/site/theme/theme.ts              institute websites (vars on .site-root)
 *
 * Every value is a CSS-variable value global.css understands: colours as the
 * "H S% L%" triplet shape, sizes in px/rem. Plain data + pure helpers, no React.
 */

export type ColorKey = "blue" | "purple" | "green" | "orange" | "red" | "teal" | "pink" | "indigo";
export type FontKey = "inter" | "roboto" | "open-sans" | "lato" | "poppins" | "system";
export type FontSizeKey = "small" | "medium" | "large" | "extra-large";
export type RadiusKey = "none" | "small" | "medium" | "large" | "full";
export type DensityKey = "compact" | "comfortable" | "spacious";
export type ModeKey = "light" | "dark" | "auto";

/** Colour presets as global.css tokens ("H S% L%"); the swatches show exactly these. */
export const COLOR_PRESETS: { key: ColorKey; label: string; hsl: string }[] = [
  { key: "blue", label: "Blue", hsl: "220 87% 55%" },
  { key: "purple", label: "Purple", hsl: "258 90% 66%" },
  { key: "green", label: "Green", hsl: "142 76% 36%" },
  { key: "orange", label: "Orange", hsl: "25 95% 53%" },
  { key: "red", label: "Red", hsl: "0 84% 60%" },
  { key: "teal", label: "Teal", hsl: "173 80% 40%" },
  { key: "pink", label: "Pink", hsl: "330 81% 60%" },
  { key: "indigo", label: "Indigo", hsl: "239 84% 67%" },
];
export const COLOR_HSL: Record<ColorKey, string> = Object.fromEntries(COLOR_PRESETS.map((c) => [c.key, c.hsl])) as Record<ColorKey, string>;

/** "220 87% 55%" -> "#2f6ef0". */
export const tokenToHex = (token: string): string => {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(token);
  if (!m) return "#000000";
  const h = Number(m[1]);
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
};

/** The preset whose colour is this hex (case-insensitive), if any. */
export const presetForHex = (hex: string): ColorKey | null =>
  COLOR_PRESETS.find((c) => tokenToHex(c.hsl).toLowerCase() === String(hex || "").toLowerCase())?.key ?? null;

const SYSTEM_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';

/** Only fonts index.html loads; each ends on a concrete system stack (no reflow while loading). */
export const FONT_OPTIONS: { key: FontKey; label: string; description: string; stack: string }[] = [
  { key: "inter", label: "Inter", description: "Modern and clean (Default)", stack: `"Inter", "Inter Fallback", ${SYSTEM_STACK}` },
  { key: "roboto", label: "Roboto", description: "Google's signature font", stack: `"Roboto", ${SYSTEM_STACK}` },
  { key: "open-sans", label: "Open Sans", description: "Friendly and readable", stack: `"Open Sans", ${SYSTEM_STACK}` },
  { key: "lato", label: "Lato", description: "Professional and elegant", stack: `"Lato", ${SYSTEM_STACK}` },
  { key: "poppins", label: "Poppins", description: "Geometric and modern", stack: `"Poppins", ${SYSTEM_STACK}` },
  { key: "system", label: "System Font", description: "Use device default font", stack: SYSTEM_STACK },
];
export const FONT_STACK: Record<FontKey, string> = Object.fromEntries(FONT_OPTIONS.map((f) => [f.key, f.stack])) as Record<FontKey, string>;

/**
 * Text size steps. The dashboard reads at 14px; a public website one step up
 * (16px), same ratios, so both scale the same way.
 */
export const FONT_SIZE_PX: Record<"dashboard" | "site", Record<FontSizeKey, number>> = {
  dashboard: { small: 12, medium: 14, large: 16, "extra-large": 18 },
  site: { small: 14, medium: 16, large: 18, "extra-large": 20 },
};
export const FONT_SIZE_LABEL: Record<FontSizeKey, string> = { small: "Small", medium: "Medium", large: "Large", "extra-large": "Extra Large" };

/** global.css's derived sizes (.text-xs ... .text-3xl), declared wherever --font-size-base is. */
export const fontSizeVars = (basePx: number): Record<string, string> => ({
  "--font-size-base": `${basePx}px`,
  "--font-size-xs": `${basePx * 0.75}px`,
  "--font-size-sm": `${basePx * 0.875}px`,
  "--font-size-lg": `${basePx * 1.125}px`,
  "--font-size-xl": `${basePx * 1.25}px`,
  "--font-size-2xl": `${basePx * 1.5}px`,
  "--font-size-3xl": `${basePx * 1.875}px`,
});

export const RADIUS_OPTIONS: { key: RadiusKey; label: string; css: string }[] = [
  { key: "none", label: "None - Sharp corners", css: "0px" },
  { key: "small", label: "Small - 2px", css: "2px" },
  { key: "medium", label: "Medium - 6px (Default)", css: "6px" },
  { key: "large", label: "Large - 12px", css: "12px" },
  { key: "full", label: "Full - Rounded", css: "9999px" },
];
export const RADIUS_CSS: Record<RadiusKey, string> = Object.fromEntries(RADIUS_OPTIONS.map((r) => [r.key, r.css])) as Record<RadiusKey, string>;

export const DENSITY_OPTIONS: { key: DensityKey; label: string; spacing: Record<string, string> }[] = [
  { key: "compact", label: "Compact - Tight spacing",
    spacing: { "--spacing-xs": "0.125rem", "--spacing-sm": "0.25rem", "--spacing-md": "0.5rem", "--spacing-lg": "0.75rem", "--spacing-xl": "1rem" } },
  { key: "comfortable", label: "Comfortable - Default",
    spacing: { "--spacing-xs": "0.25rem", "--spacing-sm": "0.5rem", "--spacing-md": "1rem", "--spacing-lg": "1.5rem", "--spacing-xl": "2rem" } },
  { key: "spacious", label: "Spacious - Loose spacing",
    spacing: { "--spacing-xs": "0.5rem", "--spacing-sm": "0.75rem", "--spacing-md": "1.5rem", "--spacing-lg": "2rem", "--spacing-xl": "3rem" } },
];
export const DENSITY_SPACING: Record<DensityKey, Record<string, string>> =
  Object.fromEntries(DENSITY_OPTIONS.map((d) => [d.key, d.spacing])) as Record<DensityKey, Record<string, string>>;

export const MODE_OPTIONS: { key: ModeKey; label: string; description: string }[] = [
  { key: "light", label: "Light Theme", description: "Clean white background for bright environments" },
  { key: "dark", label: "Dark Theme", description: "Dark background for low light environments" },
  { key: "auto", label: "Auto Theme", description: "Automatically matches your system preference" },
];
